<template>
    <div class="lounge-product-card" data-test="lounge-product-card">
        <div class="card-image-wrap">
            <div class="card-image-container">
                <NuxtLink class="card-image-link" :to="getInternalLink(blok.link)">
                    <NuxtImg
                        v-if="blok?.image?.filename"
                        :src="blok.image.filename"
                        :title="blok.image.title"
                        :alt="blok.image.alt"
                        provider="storyblok"
                        format="webp"
                        loading="lazy"
                        densities="x1"
                    />
                </NuxtLink>
            </div>
        </div>
        <div class="card-content-wrap">
            <div class="text-wrap">
                <h3 class="title">
                    <NuxtLink :to="getInternalLink(blok.link)">{{ blok.title }}</NuxtLink>
                </h3>
                <p class="description">{{ blok.description }}</p>
            </div>
            <ul class="price-list">
                <li class="price-item">
                    <span class="price-title">{{ $t('lounge.adultFrom') }}</span>
                    <span class="price">{{ blok.minAdultPrice }}</span>
                </li>
                <li class="price-item">
                    <span class="price-title">{{ $t('lounge.childFrom') }}</span>
                    <span class="price">{{ blok.minChildPrice }}</span>
                </li>
                <li class="price-item">
                    <span class="price-title">{{ $t('lounge.InfantFrom') }}</span>
                    <span class="price">{{ blok.minInfantPrice }}</span>
                </li>
            </ul>
            <NuxtLink :to="getInternalLink(blok.link)" class="link-btn">{{ blok.link_text }}</NuxtLink>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { getInternalLink } from '@nuxt-monorepo/ui/utils/internalLinking';
import type { ILoungeProductCard } from '@nuxt-monorepo/ui/types/components/ProductsCards/LoungeProductCard';

defineProps<{
    blok: ILoungeProductCard;
}>();
</script>

<style lang="postcss" scoped>
.lounge-product-card {
    @apply flex flex-col md:flex-row p-[15px] md:pr-5 md:pb-[9px] lg:pb-[15px] overflow-hidden;
    @apply rounded border border-solid border-[#e2e2e2] bg-gray;
    .card-image-wrap {
        @apply w-full h-[192px] md:w-[309px] md:h-[124px];
        img {
            @apply flex self-center md:inline-block md:max-h-full md:max-w-full;
        }
    }
    .card-image-container {
        @apply w-full h-full flex items-center justify-center;
    }
    .card-image-link {
        @apply flex justify-center overflow-hidden w-full md:w-auto h-full md:h-auto;
    }
    .card-content-wrap {
        @apply w-full overflow-hidden py-[5px] px-[18px] lg:pb-4 lg:pl-[22px] flex lg:gap-5 flex-col lg:flex-row relative;
    }
    .text-wrap {
        @apply grow basis-full;
    }
    .price-list {
        @apply hidden xl:flex grow basis-full py-1 px-5 text-[#666666] font-semibold relative;

        &:before,
        &:after {
            content: '';
            @apply top-0 bottom-0 w-0.5 bg-[#e2e2e2] absolute;
        }
        &:before {
            @apply left-0;
        }
        &:after {
            @apply right-0;
        }

        .price-item {
            @apply px-3;
        }

        .price-title {
            @apply text-sm block mb-1.5;
        }
        .price {
            @apply text-[25px] leading-[28px];
        }
    }

    .title {
        @apply text-primary hover:text-[#0056b3] text-xl leading-6 font-semibold mb-1;
    }
    .description {
        @apply text-[15px] text-[#454545] leading-5 font-semibold;
    }
    .link-btn {
        @apply text-base lg:text-xl leading-5 lg:leading-6 font-bold text-center text-white uppercase self-start;
        @apply mt-2.5 pt-3 px-[25px] pb-[10px] lg:py-[14px] lg:pr-[30px] lg:pl-[38px] bg-primary hover:bg-secondary rounded-md shrink-0;
        transition: all 0.4s ease;
    }
}
</style>
